import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Trash2, GripVertical, ImageIcon } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { BulkDeliveryUpload } from './BulkDeliveryUpload';
import { DeliverySenders } from './DeliverySenders';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };

const DeliveryTile = ({ d, index, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: d.id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 20 : undefined };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group bg-white border rounded-xl overflow-hidden select-none ${isDragging ? 'shadow-2xl ring-2 ring-[#1a5c3b] opacity-90' : 'shadow-sm'}`}
      data-testid={`delivery-row-${d.id}`}
    >
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing touch-none" data-testid={`delivery-drag-${d.id}`}>
        <img src={`${BASE}${d.image_url}`} alt="" draggable={false} className="w-full h-32 object-cover bg-gray-100" />
        <div className="flex items-center gap-1.5 px-2 py-1.5 text-xs text-gray-600">
          <GripVertical className="w-4 h-4 text-gray-400" />
          <span className="font-semibold">#{index + 1}</span>
        </div>
      </div>
      <button
        type="button"
        onClick={() => onDelete(d.id)}
        className="absolute top-2 right-2 p-1.5 rounded-full bg-white/90 text-red-600 shadow hover:bg-red-50"
        data-testid={`delivery-delete-${d.id}`}
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
};

export const DeliveriesAdmin = () => {
  const [items, setItems] = useState([]);
  const load = () => axios.get(`${API}/deliveries`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const next = arrayMove(items, items.findIndex((d) => d.id === active.id), items.findIndex((d) => d.id === over.id));
    setItems(next);
    await axios.post(`${API}/admin/deliveries/reorder`, { ids: next.map((d) => d.id) }, cfg).catch(load);
  };
  const remove = async (id) => {
    if (!window.confirm('Supprimer cette livraison du site?')) return;
    await axios.delete(`${API}/admin/deliveries/${id}`, cfg);
    load();
  };

  return (
    <div className="p-6 space-y-6" data-testid="deliveries-admin">
      <div>
        <h2 className="text-lg font-bold text-gray-900">Nos livraisons récentes</h2>
        <p className="text-sm text-gray-500">Ces photos apparaissent sur la page d’accueil dans cet ordre. Glissez une photo pour changer sa place. La section est masquée s’il n’y a aucune photo.</p>
      </div>
      <BulkDeliveryUpload onDone={load} />
      <DeliverySenders />
      {items.length === 0 ? (
        <div className="border rounded-xl p-10 text-center text-gray-400" data-testid="delivery-list"><ImageIcon className="w-8 h-8 mx-auto mb-2" />Aucune livraison pour l’instant</div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={items.map((d) => d.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="delivery-list">
              {items.map((d, i) => <DeliveryTile key={d.id} d={d} index={i} onDelete={remove} />)}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};
